import { createContext, PropsWithChildren, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useArchiveContext } from './ArchiveContext'
import { useApiContext } from './ApiContext'
import DataContextValue from './DataContextValue'
import { useToastContext } from './ToastContext'
import { useUserContext } from './UserContext'
import { useAction } from '../../hooks/action'
import { useProvideContext } from '../../hooks/context'
import { useImmutableObject } from '../../hooks/immutable'
import { usePaginatedTable } from '../../hooks/pagination'
import { mapReadResponse, mapReadTableResponse, throwIfResponseNotOk } from '../../tools/api'
import Calibration from '../../models/Calibration'
import Sensor from '../../models/Sensor'
import TableControl from '../../models/TableControl'
import { unpackAllSensorsTableSpec } from '../table/table-spec'

export interface SensorContextValue extends DataContextValue {
    selectedSensor?: Sensor
    selectedSensorCalibrations: TableControl<Calibration>
    sensors: TableControl<Sensor>
    archiveSensor: (sensorId?: string) => Promise<void>
    restoreSensor: (sensorId?: string) => Promise<void>
    selectedSensorCalibrationsLoading: boolean
    sensorsLoading: boolean
}

const SensorContext = createContext<SensorContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const params = useParams()
    const { includeArchived } = useArchiveContext()
    const { sensorApi, calibrationApi } = useApiContext()
    const { selectedOrganization } = useUserContext()
    const Toast = useToastContext()
    const sensorId = params.sensorId

    const [sensors, sensorsLoading, _sensorsError, refreshSensors] = usePaginatedTable(
        async (table: TableControl<Sensor>) => {
            const orgId = selectedOrganization?.id
            if (!orgId) return null
            const { size, frequency } = unpackAllSensorsTableSpec(table)
            const response = await sensorApi.getSensorsOfTenantV2({
                tenantId: orgId,
                sortBy: table.sortBy,
                ascending: table.ascending,
                size: size?.[0],
                frequency: frequency?.[0],
                skip: table.skip,
                take: table.take,
                includeArchived,
            })
            return mapReadTableResponse(response, table, Sensor)
        },
        [selectedOrganization?.id, includeArchived]
    )

    const [selectedSensor, selectedLoading, selectedError, refreshSelectedSensor] =
        useImmutableObject(async () => {
            const orgId = selectedOrganization?.id
            if (!(orgId && sensorId)) return
            const response = await sensorApi.getSensorV2({ sensorId, tenantId: orgId })
            return mapReadResponse(response, Sensor)
        }, [sensorId, selectedOrganization?.id])

    const [selectedSensorCalibrations, selectedSensorCalibrationsLoading, calibrationsError] =
        usePaginatedTable(
            async (table: TableControl<Calibration>) => {
                const orgId = selectedOrganization?.id
                if (!(orgId && sensorId)) return null
                const response = await calibrationApi.getCalibrationsV2({
                    tenantId: orgId,
                    sensorId,
                    skip: table.skip,
                    take: table.take,
                })
                return mapReadTableResponse(response, table, Calibration)
            },
            [sensorId, selectedOrganization?.id]
        )

    const [archiveLoading, archiveError, archiveSensor] = useAction(async (sensorId?: string) => {
        const orgId = selectedOrganization?.id
        if (!(orgId && sensorId)) return
        const response = await sensorApi.archiveSensorV2({ sensorId, tenantId: orgId })
        throwIfResponseNotOk(response)
        if (sensorId === selectedSensor?.id) refreshSelectedSensor()
        refreshSensors()
        Toast.completed('archive', 'sensor')
    })

    const [restoreLoading, restoreError, restoreSensor] = useAction(async (sensorId?: string) => {
        const orgId = selectedOrganization?.id
        if (!(orgId && sensorId)) return
        const response = await sensorApi.restoreSensorV2({ sensorId, tenantId: orgId })
        throwIfResponseNotOk(response)
        if (sensorId === selectedSensor?.id) refreshSelectedSensor()
        refreshSensors()
        Toast.completed('restore', 'sensor')
    })

    const loading =
        selectedLoading || selectedSensorCalibrationsLoading || archiveLoading || restoreLoading
    const error = selectedError || calibrationsError || archiveError || restoreError

    useEffect(() => {
        if (error) Toast.error(error, 'Sensor error')
    }, [error])

    const value: SensorContextValue = {
        loading,
        error,

        sensors,
        selectedSensor,
        selectedSensorCalibrations,
        archiveSensor,
        restoreSensor,
        selectedSensorCalibrationsLoading,
        sensorsLoading,
    }

    return <SensorContext.Provider value={value}>{children}</SensorContext.Provider>
}

export const useSensorContext = () => useProvideContext(SensorContext, 'Sensor')
